import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiLink, FiCpu, FiUsers, FiDollarSign, FiShield, FiTrendingUp, FiMic, FiGlobe, FiCalendar, FiCheckCircle, FiFileText, FiImage, FiTarget, FiPercent, FiGrid, FiBookOpen, FiTwitter, FiLinkedin, FiSend, FiMail, FiMapPin, FiMenu, FiX, FiSearch, FiChevronLeft, FiChevronRight, FiPlay, FiAward } from 'react-icons/fi';
import styles from './Landing.module.css';
import Reveal from '../../components/Reveal/Reveal';
import Select from '../../components/Select/Select';
import CountUp from '../../components/CountUp/CountUp';
import { toast } from 'react-toastify';
import axiosClient from '../../api/axiosClient';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');

// Advisory Board ranks shown on the landing page (same numbers as the system's rank rules).
const ADVISORY_RANKS = [
  { n: 1, name: 'Executive',         self: '$500',   team: '$25K',      selfPct: '3%', teamPct: '1%', color: '#1d60f6' },
  { n: 2, name: 'Exec. Manager',     self: '$1,000', team: '$1 Lakh',   selfPct: '3%', teamPct: '1%', color: '#7c5cff' },
  { n: 3, name: 'Community Manager', self: '$1,500', team: '$2.5 Lakh', selfPct: '3%', teamPct: '1%', color: '#2fa7a0' },
  { n: 4, name: 'Country Manager',   self: '$2,500', team: '$5 Lakh',   selfPct: '3%', teamPct: '1%', color: '#d98c2b' },
  { n: 5, name: 'Global Promoter',   self: '$5,000', team: '$10 Lakh',  selfPct: '3%', teamPct: '1%', color: '#e8b64a' },
];

const Landing: React.FC = () => {
  const [meetingType, setMeetingType] = useState<'physical' | 'vc'>('physical');
  const [meetingSubmitted, setMeetingSubmitted] = useState(false);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [meetingError, setMeetingError] = useState('');
  const [partnerTier, setPartnerTier] = useState('');
  const [partnerTiers, setPartnerTiers] = useState<any[]>([]);
  const [raiseTargetUsd, setRaiseTargetUsd] = useState<number | null>(null);
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [blogLoading, setBlogLoading] = useState(true);
  const [blogCategory, setBlogCategory] = useState('All Blogs');
  const [blogCategories, setBlogCategories] = useState<string[]>(['Blockchain', 'Crypto', 'DeFi', 'AI', 'Security', 'Guides']);
  const [blogSearch, setBlogSearch] = useState('');
  const [activeBlogIndex, setActiveBlogIndex] = useState(0);
  const [storyVideos, setStoryVideos] = useState<any[]>([]);
  const [storyLoading, setStoryLoading] = useState(true);
  const [partnerLogos, setPartnerLogos] = useState<any[]>([]);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [companyDocs, setCompanyDocs] = useState<any[]>([]);
  const [productDocs, setProductDocs] = useState<any[]>([]);
  const [pitchDeckDocs, setPitchDeckDocs] = useState<any[]>([]);
  const [investReferral, setInvestReferral] = useState('');
  const [defaultRefCode, setDefaultRefCode] = useState('');

  useEffect(() => {
    axiosClient.get('/partners/tiers').then(({ data }) => {
      setPartnerTiers(data.tiers);
      if (data.raiseTargetUsd) setRaiseTargetUsd(Number(data.raiseTargetUsd));
    }).catch(() => {});
    axiosClient.get('/company-documents', { params: { category: 'company' } }).then(({ data }) => setCompanyDocs(data.documents)).catch(() => {});
    axiosClient.get('/company-documents', { params: { category: 'product' } }).then(({ data }) => setProductDocs(data.documents)).catch(() => {});
    axiosClient.get('/company-documents', { params: { category: 'pitch_deck' } }).then(({ data }) => setPitchDeckDocs(data.documents)).catch(() => {});
    axiosClient.get('/settings/referral-code').then(({ data }) => setDefaultRefCode(data.defaultReferralCode)).catch(() => {});
    axiosClient.get('/content/story-videos').then(({ data }) => setStoryVideos(data.videos)).catch(() => {}).finally(() => setStoryLoading(false));
    axiosClient.get('/content/partner-logos').then(({ data }) => setPartnerLogos(data.logos)).catch(() => {});
    axiosClient.get('/content/blog-categories').then(({ data }) => {
      if (data.categories?.length) setBlogCategories(data.categories);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      axiosClient.get('/content/blog-posts', { params: { category: blogCategory, search: blogSearch || undefined } })
        .then(({ data }) => setBlogPosts(data.posts)).catch(() => {}).finally(() => setBlogLoading(false));
    }, blogSearch ? 350 : 0);
    return () => clearTimeout(t);
  }, [blogCategory, blogSearch]);

  useEffect(() => { setActiveBlogIndex(0); }, [blogPosts]);

  useEffect(() => {
    if (blogPosts.length < 2) return;
    const t = setInterval(() => {
      setActiveBlogIndex((i) => (i + 1) % blogPosts.length);
    }, 5000);
    return () => clearInterval(t);
  }, [blogPosts]);

  const [investSubmitted, setInvestSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [investLoading, setInvestLoading] = useState(false);
  const [investError, setInvestError] = useState('');
  const [calcPortal, setCalcPortal] = useState<'A' | 'B' | 'C'>('A');
  const [calcCurrency, setCalcCurrency] = useState<'INR' | 'USD'>('INR');
  const [calcAmount, setCalcAmount] = useState('');

  const range = calcCurrency === 'INR' ? { min: 25000, max: 2500000 } : { min: 250, max: 25000 };
  const amountNum = parseFloat(calcAmount);
  const amountValid = !isNaN(amountNum) && amountNum >= range.min && amountNum <= range.max;
  // monthly = what is paid every month (capital part + profit), maturity = extra capital paid in the
  // last month. Portal C pays profit only, and its whole capital comes back at maturity.
  const PORTAL_CALC = {
    A: { monthly: 0.07, maturity: 0.25, token: 0.03, term: 25 },
    B: { monthly: 0.05, maturity: 0.70, token: 0.05, term: 15 },
    C: { monthly: 0.05, maturity: 1.00, token: 0.05, term: 25 },
  } as const;
  const portalCfg = PORTAL_CALC[calcPortal];
  const fmt = (n: number) => (calcCurrency === 'INR' ? `₹${Math.round(n).toLocaleString('en-IN')}` : `$${Math.round(n).toLocaleString('en-US')}`);

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterSubmitting(true);
    try {
      await axiosClient.post('/content/newsletter', { email: newsletterEmail.trim() });
      toast.success("You're subscribed! Watch your inbox for updates.");
      setNewsletterEmail('');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Could not subscribe right now.');
    } finally {
      setNewsletterSubmitting(false);
    }
  };

  const handleMeetingSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMeetingError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    setMeetingLoading(true);
    try {
      await axiosClient.post('/leads', {
        leadType: 'meeting',
        meetingType,
        fullName: data.get('fullName'),
        email: data.get('email'),
        phone: data.get('phone'),
        fundOrg: data.get('fundOrg'),
        message: [
          `Preferred date: ${data.get('date') || 'flexible'}`,
          data.get('city') ? `City/TZ: ${data.get('city')}` : '',
          data.get('notes') || '',
        ].filter(Boolean).join(' | '),
      });
      if (partnerTier) {
        try {
          await axiosClient.post('/partners/interest', {
            tier: partnerTier,
            fullName: data.get('fullName'),
            email: data.get('email'),
            phone: data.get('phone'),
            fundOrg: data.get('fundOrg'),
            meetingType,
            preferredDate: data.get('date') || null,
            city: data.get('city') || null,
            message: data.get('notes') || null,
          });
        } catch {
          // Non-fatal — the meeting request itself already succeeded above.
        }
      }
      setMeetingSubmitted(true);
      toast.success('Meeting request submitted!');
      form.reset();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setMeetingError(msg);
      toast.error(msg);
    } finally {
      setMeetingLoading(false);
    }
  };

  const handleInvestSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setInvestError('');
    const form = e.currentTarget;
    const data = new FormData(form);
    setInvestLoading(true);
    try {
      await axiosClient.post('/leads', {
        leadType: 'invest',
        fullName: data.get('fullName'),
        email: data.get('email'),
        phone: data.get('phone'),
        fundOrg: data.get('city'),
        amount: data.get('amount'),
        currency: data.get('currency') || 'INR',
        message: [
          `Portal: ${data.get('portal')}`,
          `Currency: ${data.get('currency')}`,
          data.get('referral') ? `Referral: ${data.get('referral')}` : '',
          data.get('notes') || '',
        ].filter(Boolean).join(' | '),
      });
      setInvestSubmitted(true);
      toast.success('Investment interest submitted!');
      form.reset();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
      setInvestError(msg);
      toast.error(msg);
    } finally {
      setInvestLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <nav className={styles.nav}>
        <div className={styles.brand}>
          <img src="/images/logo.png" alt="BCT" className={styles.brandLogo} />
          <span className={styles.brandFull}>Block Cost Technologies</span>
          <span className={styles.brandShort}>BCT</span>
        </div>
        <div className={styles.navLinks}>
          <a href="#opportunity">Opportunity</a>
          <a href="#portals">Portals</a>
          <a href="#rewards">Rewards</a>
          <a href="#services">Services</a>
          <a href="#partners">Partners</a>
          <a href="#product">Product</a>
          <a href="#pitch-deck">Pitch Deck</a>
          <a href="#bct100">Token</a>
          <a href="#documents">Documents</a>
          <a href="#meetings">Meetings</a>
        </div>
        <div className={styles.navRight}>
          <Link to="/login" className={styles.navLogin}>Login</Link>
          <a href="#invest" className={styles.navCta}>Register interest</a>
          <button
            type="button"
            className={styles.navHamburger}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileMenuOpen((v) => !v)}
          >
            {mobileMenuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className={styles.mobileMenu}>
          <a href="#opportunity" onClick={() => setMobileMenuOpen(false)}>Opportunity</a>
          <a href="#portals" onClick={() => setMobileMenuOpen(false)}>Portals</a>
          <a href="#rewards" onClick={() => setMobileMenuOpen(false)}>Rewards</a>
          <a href="#services" onClick={() => setMobileMenuOpen(false)}>Services</a>
          <a href="#partners" onClick={() => setMobileMenuOpen(false)}>Partners</a>
          <a href="#product" onClick={() => setMobileMenuOpen(false)}>Product</a>
          <a href="#pitch-deck" onClick={() => setMobileMenuOpen(false)}>Pitch Deck</a>
          <a href="#bct100" onClick={() => setMobileMenuOpen(false)}>Token</a>
          <a href="#documents" onClick={() => setMobileMenuOpen(false)}>Documents</a>
          <a href="#meetings" onClick={() => setMobileMenuOpen(false)}>Meetings</a>
          <div className={styles.mobileMenuActions}>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
            <a href="#invest" className={styles.navCta} onClick={() => setMobileMenuOpen(false)}>Register interest</a>
          </div>
        </div>
      )}

      <header className={styles.hero}>
        <video
          className={styles.heroBgVideo}
          src="/videos/hero-bg.mp4"
          poster="/images/hero-bg-nebula.png"
          autoPlay
          muted
          loop
          playsInline
        />
        <div className={styles.heroBgTint} />
        <div className={`${styles.heroBlob} ${styles.heroBlob1}`} />
        <div className={`${styles.heroBlob} ${styles.heroBlob2}`} />
        <div className={`${styles.heroBlob} ${styles.heroBlob3}`} />
        <div className={styles.heroGrid}>
        <div className={styles.heroInner}>
          <Reveal>
            <div className={styles.heroEyebrow}><span />Blockchain · AI · Web3</div>
          </Reveal>
          <Reveal delay={80}>
            <h1 className={styles.heroTitle}>
              Fundraising for the <em>next era</em> of Web3 infrastructure.
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className={styles.heroSub}>
              Block Cost Technologies is raising $25M to build blockchain, AI-agent, fintech and
              Web3 tooling — with structured investor payouts across two portal options.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className={styles.heroActions}>
              <a href="#invest" className={styles.btnPrimary}>Register interest <FiArrowRight /></a>
              <a href="#portals" className={styles.btnGhost}>View portals</a>
            </div>
          </Reveal>
        </div>
        {/* <Reveal delay={200} className={styles.heroVisual}>
          <div className={styles.heroSphereWrap}>
            <img src="/images/logo.png" alt="BCT" className={styles.heroSphereImg} />
            <div className={styles.heroBase} />
          </div>
        </Reveal> */}
        </div>
      </header>

      {/* ---------- Opportunity ---------- */}
      <section id="opportunity" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>The opportunity</div>
          <h2 className={styles.sectionTitle}>A $25M raise, built for structured returns.</h2>
          <p className={styles.sectionSub}>
            Two ticket-size bands, two payout structures — designed for investors who want either
            monthly liquidity or a maturity-focused position.
          </p>
        </Reveal>
        <div className={styles.statGrid}>
          {[
            { n: 25, prefix: '$', suffix: 'M', label: 'Raise target', Icon: FiTarget, color: '#1d60f6' },
            { n: 15, prefix: undefined as string | undefined, suffix: 'mo', label: 'Month payout cycle', Icon: FiCalendar, color: '#7c5cff' },
            { n: 7, prefix: undefined as string | undefined, suffix: '%', label: 'Max monthly ROI', Icon: FiPercent, color: '#4fae7a' },
            { n: 5, prefix: undefined as string | undefined, suffix: undefined as string | undefined, label: 'Corporate divisions', Icon: FiGrid, color: '#d98c2b' },
          ].map((s, i) => (
            <Reveal key={s.label} delay={i * 70} className={styles.statCard} style={{ ['--accent' as any]: s.color }}>
              <div className={styles.statIconWrap}><s.Icon /></div>
              <div className={styles.statCardNum}>
                <CountUp end={s.n} prefix={s.prefix} suffix={s.suffix} />
              </div>
              <div className={styles.statCardLbl}>{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Portals ---------- */}
      <section id="portals" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Investment portals</div>
          <h2 className={styles.sectionTitle}>Retail Investor</h2>
          <p className={styles.sectionSubHeading}>Choose Portal A, Portal B or Portal C.</p>
          <p className={styles.sectionSub}>Portal A starts from just ₹10,000 — add ₹15,000 later to complete a full ₹25,000 Portal. Full tickets: ₹25K – ₹25 Lac / $250 – $25,000, across any structure below.</p>
        </Reveal>
        <div className={`${styles.portalsGrid} ${styles.portalsGrid3}`}>
          <Reveal className={styles.portal}>
            <span className={styles.portalBadge}>Portal A</span>
            <h3 className={styles.portalTitle}>Monthly capital + profit</h3>
            <div className={styles.portalRoi}>7% <span>/ month for 25 months</span></div>
            <ul className={styles.portalList}>
              <li>3% capital + 4% profit paid every month</li>
              <li>75% of total capital returned across the term</li>
              <li>Remaining 25% capital paid on maturity</li>
              <li>Instant 3% reward in BCT100 token</li>
            </ul>
          </Reveal>
          <Reveal delay={100} className={`${styles.portal} ${styles.portalB}`}>
            <span className={`${styles.portalBadge} ${styles.portalBadgeB}`}>Portal B</span>
            <h3 className={styles.portalTitle}>Monthly capital + profit</h3>
            <div className={`${styles.portalRoi} ${styles.portalRoiB}`}>5% <span>/ month for 15 months</span></div>
            <ul className={styles.portalList}>
              <li>2% capital + 3% profit paid every month</li>
              <li>30% of total capital returned across the term</li>
              <li>Remaining 70% capital paid on maturity</li>
              <li>Instant 5% reward in BCT100 token</li>
            </ul>
          </Reveal>
          <Reveal delay={200} className={`${styles.portal} ${styles.portalC}`}>
            <span className={`${styles.portalBadge} ${styles.portalBadgeC}`}>Portal C</span>
            <h3 className={styles.portalTitle}>Profit sharing</h3>
            <div className={`${styles.portalRoi} ${styles.portalRoiC}`}>5% <span>/ month for 25 months</span></div>
            <ul className={styles.portalList}>
              <li>5% profit paid every month</li>
              <li>No capital returned during the term</li>
              <li>100% capital paid back at the end of 25 months</li>
              <li>Instant 5% reward in BCT100 token</li>
            </ul>
          </Reveal>
        </div>
        <Reveal delay={100} className={`${styles.portal} ${styles.starterCard}`}>
          <div>
            <span className={`${styles.portalBadge} ${styles.portalBadgeS}`}>Starter</span>
            <h3 className={styles.portalTitle}>Start small in Portal A</h3>
            <div className={`${styles.portalRoi} ${styles.portalRoiS}`}>₹10,000 <span>to start, in Portal A</span></div>
          </div>
          <ul className={styles.portalList}>
            <li>Full Portal A ROI, BCT100 and rewards on your ₹10,000 from day one</li>
            <li>Next deposit in Portal A: ₹15,000 — it earns from the day you add it</li>
            <li>Together they make a ₹25,000 Portal. After that, invest ₹25,000 or more as usual</li>
          </ul>
        </Reveal>
      </section>

      {/* ---------- BCT100 Token ---------- */}
      <section id="bct100" className={styles.section}>
        <div className={styles.tokenShowcase}>
          <Reveal className={styles.tokenVisual}>
            <div className={styles.tokenPanel}>
              <img src="/images/tokens/bct100-silver.png" alt="BCT100 Token" className={styles.tokenImg} />
            </div>
          </Reveal>
          <Reveal className={styles.tokenCopy}>
            <div className={styles.eyebrow}>Reward token</div>
            <h2 className={styles.sectionTitle}>Every investment earns BCT100.</h2>
            <p className={styles.sectionSub}>
              An instant token reward on your Portal A, B or C investment — claimable anytime, priced live by
              BCT Platform.
            </p>
            <div className={styles.tokenPillRow}>
              <div className={styles.tokenPill}><b>3%</b> instant BCT100 on Portal A</div>
              <div className={styles.tokenPill}><b>5%</b> instant BCT100 on Portal B</div>
              <div className={styles.tokenPill}><b>5%</b> instant BCT100 on Portal C</div>
            </div>
            <p className={styles.tokenNote}>Token price is set and updated by the platform — visible before you claim.</p>
            <p className={styles.tokenNote}>Tokens are given on every deposit, including the ₹10,000 Starter and the ₹15,000 top-up.</p>
          </Reveal>
        </div>
      </section>

      {/* ---------- Rewards ---------- */}
      <section id="rewards" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Introducer's bonus</div>
          <h2 className={styles.sectionTitle}>Affiliate &amp; team rewards.</h2>
          <p className={styles.sectionSub}>Investors who bring in referrals earn instant bonuses plus a share of team-level activity for the term of their portal.</p>
        </Reveal>

        <div className={`${styles.portalsGrid} ${styles.portalsGrid3}`}>
          <Reveal className={styles.portal}>
            <span className={styles.portalBadge}>Portal A</span>
            <h3 className={styles.portalTitle}>Affiliate bonus</h3>
            <p className={styles.rewardsIntro}>5% instant bonus on every direct referral, plus monthly team overrides for 12 months on 7 levels. Your direct referral is your 1st team, so you earn both:</p>
            <div className={styles.lvlRow}>
              <div className={styles.lvlChip}><b>0.75%</b> 1st team (direct)</div>
              <div className={styles.lvlChip}><b>0.50%</b> 2nd team</div>
              <div className={styles.lvlChip}><b>0.25%</b> 3rd team</div>
              <div className={styles.lvlChip}><b>0.15%</b> 4th team</div>
              <div className={styles.lvlChip}><b>0.10%</b> 5th team</div>
              <div className={styles.lvlChip}><b>0.08%</b> 6th team</div>
              <div className={styles.lvlChip}><b>0.07%</b> 7th team</div>
            </div>
          </Reveal>
          <Reveal delay={100} className={`${styles.portal} ${styles.portalB}`}>
            <span className={`${styles.portalBadge} ${styles.portalBadgeB}`}>Portal B</span>
            <h3 className={styles.portalTitle}>Affiliate bonus</h3>
            <p className={styles.rewardsIntro}>7% instant bonus on every direct referral, plus monthly team overrides for 12 months on 7 levels. Your direct referral is your 1st team, so you earn both:</p>
            <div className={styles.lvlRow}>
              <div className={styles.lvlChip}><b>1.00%</b> 1st team (direct)</div>
              <div className={styles.lvlChip}><b>0.75%</b> 2nd team</div>
              <div className={styles.lvlChip}><b>0.50%</b> 3rd team</div>
              <div className={styles.lvlChip}><b>0.25%</b> 4th team</div>
              <div className={styles.lvlChip}><b>0.15%</b> 5th team</div>
              <div className={styles.lvlChip}><b>0.08%</b> 6th team</div>
              <div className={styles.lvlChip}><b>0.07%</b> 7th team</div>
            </div>
          </Reveal>
          <Reveal delay={200} className={`${styles.portal} ${styles.portalC}`}>
            <span className={`${styles.portalBadge} ${styles.portalBadgeC}`}>Portal C</span>
            <h3 className={styles.portalTitle}>Affiliate bonus</h3>
            <p className={styles.rewardsIntro}>7% instant bonus on every direct referral, plus monthly team overrides for 12 months on 7 levels. Your direct referral is your 1st team, so you earn both:</p>
            <div className={styles.lvlRow}>
              <div className={styles.lvlChip}><b>1.00%</b> 1st team (direct)</div>
              <div className={styles.lvlChip}><b>0.75%</b> 2nd team</div>
              <div className={styles.lvlChip}><b>0.50%</b> 3rd team</div>
              <div className={styles.lvlChip}><b>0.25%</b> 4th team</div>
              <div className={styles.lvlChip}><b>0.15%</b> 5th team</div>
              <div className={styles.lvlChip}><b>0.08%</b> 6th team</div>
              <div className={styles.lvlChip}><b>0.07%</b> 7th team</div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={150}>
          <div className={styles.rewardsStrip}>
            {[
              { pct: '3%', amt: '₹25 Lac', Icon: FiTarget, color: '#1d60f6' },
              { pct: '4%', amt: '₹50 Lac', Icon: FiTrendingUp, color: '#7c5cff' },
              { pct: '5%', amt: '₹125 Lac', Icon: FiPercent, color: '#4fae7a' },
            ].map((r) => (
              <div key={r.amt} className={styles.rewardTile} style={{ ['--accent' as any]: r.color }}>
                <div className={styles.rewardIcon}><r.Icon /></div>
                <div className={styles.rewardBig}>{r.pct}</div>
                <div className={styles.rewardSmall}>{r.amt}</div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={200}>
          <div className={styles.contestBanner}>
            <div>
              <h4>Contest &amp; prizes</h4>
              <p>Complete ₹1 Cr or ₹5 Cr business in 90 days for an attractive prize. Company pays the ex-showroom price.</p>
            </div>
            <div className={styles.contestPrizes}>
              <div className={styles.prizePill}>₹1 Cr → Fronx</div>
              <div className={styles.prizePill}>₹5 Cr → Skoda Slavia</div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ---------- Advisory Board ---------- */}
      <section id="advisory-board" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Advisory Board</div>
          <h2 className={styles.sectionTitle}>Rise through the Advisory Board.</h2>
          <p className={styles.sectionSub}>Grow your own investment and your team. Earn a one-time reward at every rank you reach.</p>
        </Reveal>

        <div className={styles.rankGrid}>
          {ADVISORY_RANKS.map((r, i) => (
            <Reveal
              key={r.n}
              delay={i * 80}
              className={`${styles.rankCard} ${r.n === 5 ? styles.rankTop : ''}`}
            >
              <div className={styles.rankInner} style={{ ['--accent' as any]: r.color }}>
                <div className={styles.rankHead}>
                  <span className={styles.rankMedal}>{r.n}</span>
                  <span className={styles.rankTag}>{r.n === 5 ? 'Top rank' : `Rank ${r.n}`}</span>
                </div>
                <h3 className={styles.rankName}>{r.name}</h3>

                <div className={styles.rankReward}>
                  <div className={styles.rankRewardLabel}>One-time reward</div>
                  <div className={styles.rankRewardRow}>
                    <div className={styles.rankPct}><b>{r.selfPct}</b><span>of Self</span></div>
                    <i className={styles.rankPlus}>+</i>
                    <div className={styles.rankPct}><b>{r.teamPct}</b><span>of L{r.n} Team</span></div>
                  </div>
                </div>

                <div className={styles.rankReqs}>
                  <div className={styles.rankReqRow}>
                    <span className={styles.rankReqIcon}><FiTrendingUp /></span>
                    <span className={styles.rankReqLabel}>Self</span>
                    <b>{r.self}</b>
                  </div>
                  <div className={styles.rankReqRow}>
                    <span className={styles.rankReqIcon}><FiUsers /></span>
                    <span className={styles.rankReqLabel}>L{r.n} Team</span>
                    <b>{r.team}</b>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={150}>
          <div className={styles.rankInfo}>
            <div className={styles.rankInfoItem}>
              <span className={styles.rankInfoIcon}><FiCheckCircle /></span>
              <div><b>Paid once per rank</b><p>Each rank reward is paid one time only.</p></div>
            </div>
            <div className={styles.rankInfoItem}>
              <span className={styles.rankInfoIcon}><FiTrendingUp /></span>
              <div><b>Extra on top</b><p>Paid in addition to your direct bonus and team overrides.</p></div>
            </div>
            <div className={styles.rankInfoItem}>
              <span className={styles.rankInfoIcon}><FiTarget /></span>
              <div><b>Both targets needed</b><p>Reach the Self investment and the Team business shown on the rank card.</p></div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ---------- Services ---------- */}
      <section id="services" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Services</div>
          <h2 className={styles.sectionTitle}>Everything a Web3 company needs, in-house.</h2>
        </Reveal>
        <div className={styles.servicesGrid}>
          {[
            { t: 'Blockchain development', d: 'Full-stack blockchain builds, custom chains & smart contracts.', Icon: FiLink, img: '/images/services/Blockchain_Development.png', color: '#1d60f6' },
            { t: 'AI agent development', d: 'Full-stack AI agents for automation and product workflows.', Icon: FiCpu, img: '/images/services/AI_Agent_Development.png', color: '#7c5cff' },
            { t: 'CRM solutions', d: 'Custom & full-stack CRM builds tailored to enterprise needs.', Icon: FiUsers, img: '/images/services/CRM_Solutions.png', color: '#2bd4c8' },
            { t: 'Fintech solutions', d: 'Custom, full-stack fintech products and infrastructure.', Icon: FiDollarSign, img: '/images/services/Fintech_Solutions.png', color: '#4fae7a' },
            { t: 'Data & security', d: 'Enterprise data and security solutions, deployed on-prem.', Icon: FiShield, img: '/images/services/Data_and_Security.png', color: '#d98c2b' },
            { t: 'Web3 marketing', d: 'Content, paid ads, social, influencer & community building.', Icon: FiTrendingUp, img: '/images/services/Web3_Marketing.png', color: '#e2534a' },
            { t: 'Crypto listing & PR', d: 'Exchange listings, PR & media, strategic market positioning.', Icon: FiMic, img: '/images/services/Crypto_Listing_PR.png', color: '#a855f7' },
            { t: 'Global market access', d: 'Governance solutions and international market entry support.', Icon: FiGlobe, img: '/images/services/Global_Market_Access.png', color: '#1d60f6' },
            { t: 'Events & management', d: 'End-to-end event strategy, execution and management.', Icon: FiCalendar, img: '/images/services/Events_Management.png', color: '#e0a468' },
          ].map((s, i) => (
            <Reveal key={s.t} delay={(i % 3) * 80} className={styles.serviceCard} style={{ ['--accent' as any]: s.color }}>
              <div className={styles.serviceImgWrap}>
                <img src={s.img} alt={s.t} className={styles.serviceImg} />
              </div>
              <div className={styles.serviceBody}>
                <div className={styles.serviceIconWrap}><s.Icon /></div>
                <h4>{s.t}</h4>
                <p>{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Technology Backed Future Plans ---------- */}
      <section id="tech-plans" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Infrastructure roadmap</div>
          <h2 className={styles.sectionTitle}>Our technology backed future plans.</h2>
          <p className={styles.sectionSub}>
            Physical infrastructure investments planned to power our blockchain, AI, and Web3 operations —
            fully owned and operated in-house.
          </p>
        </Reveal>

        <div className={styles.techPlanGrid}>
          <Reveal className={styles.techPlanCard}>
            <div className={styles.techPlanImgWrap}>
              <img src="/images/tech-plans/data-center.png" alt="In-House Data Center" className={styles.techPlanImg} />
            </div>
            <div className={styles.techPlanBody}>
              <h3>In-House Data Center</h3>
              <ul className={styles.techPlanList}>
                <li>200KW IT Load Data Centre setup</li>
                <li>2–5 racks — 100KW high-density DC setup for Blockchain &amp; AI</li>
                <li>10–15 racks — Tier III dedicated server storage setup</li>
                <li>CPU &amp; GPU load bank</li>
                <li>3,000–5,000 sq. ft. area required</li>
              </ul>
              <div className={styles.techPlanCost}>
                <span>Estimated cost</span>
                <b>₹25 – 35 Cr</b>
              </div>
            </div>
          </Reveal>

          <Reveal delay={100} className={styles.techPlanCard}>
            <div className={styles.techPlanImgWrap}>
              <img src="/images/tech-plans/renewable-energy.png" alt="Source of Renewable Energy" className={styles.techPlanImg} />
            </div>
            <div className={styles.techPlanBody}>
              <h3>Source of Renewable Energy</h3>
              <ul className={styles.techPlanList}>
                <li>2 MW solar renewable energy setup</li>
                <li>2–4 air wind turbines for extended UPS backup</li>
                <li>10–15 acres of land required</li>
                <li>₹5 – 10 Cr cost of establishing per MW</li>
              </ul>
              <div className={styles.techPlanCost}>
                <span>Estimated cost</span>
                <b>~₹25 Cr for 2MW</b>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- Structure ---------- */}
      <section id="structure" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Corporate structure</div>
          <h2 className={styles.sectionTitle}>One company, five divisions.</h2>
          <p className={styles.sectionSub}>Development, marketing and administration teams operate in-house across India &amp; global markets.</p>
        </Reveal>
        <Reveal className={styles.orgFanIntro}>
          <div className={styles.orgRootPill}>BLOCK COST FOUNDATION</div>
        </Reveal>
        <Reveal className={styles.orgMarqueeWrap}>
          <div className={styles.orgMarqueeTrack}>
            {[
              { name: 'BCT Markets', tag: 'Trading & exchange ops', Icon: FiTrendingUp, color: '#1d60f6' },
              { name: 'Block Cost Labs', tag: 'R&D & product engineering', Icon: FiCpu, color: '#7c5cff' },
              { name: 'BCT Academy', tag: 'Web3 & blockchain education', Icon: FiBookOpen, color: '#4fae7a' },
              { name: 'Block Cost Chain', tag: 'Custom chains & protocols', Icon: FiLink, color: '#d98c2b' },
              { name: 'Block Cost Global', tag: 'International expansion', Icon: FiGlobe, color: '#e2534a' },
            ].concat([
              { name: 'BCT Markets', tag: 'Trading & exchange ops', Icon: FiTrendingUp, color: '#1d60f6' },
              { name: 'Block Cost Labs', tag: 'R&D & product engineering', Icon: FiCpu, color: '#7c5cff' },
              { name: 'BCT Academy', tag: 'Web3 & blockchain education', Icon: FiBookOpen, color: '#4fae7a' },
              { name: 'Block Cost Chain', tag: 'Custom chains & protocols', Icon: FiLink, color: '#d98c2b' },
              { name: 'Block Cost Global', tag: 'International expansion', Icon: FiGlobe, color: '#e2534a' },
            ]).map((d, i) => (
              <div key={i} className={styles.orgMarqueeCard} style={{ ['--accent' as any]: d.color }}>
                <div className={styles.orgFanIcon}><d.Icon /></div>
                <div className={styles.orgFanName}>{d.name}</div>
                <div className={styles.orgFanTag}>{d.tag}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ---------- Partner Network ---------- */}
      <section id="partner-network" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Partner Network</h2>
        </Reveal>
        <Reveal className={styles.partnerNetworkRow}>
          <img src="/images/brand/bct-icon.png" alt="BCT" className={styles.partnerNetworkIcon} />
          <div className={styles.orgMarqueeWrap}>
            <div className={styles.orgMarqueeTrack}>
              {partnerLogos.concat(partnerLogos).map((p, i) => (
                <a
                  key={i}
                  href={p.link_url || undefined}
                  target={p.link_url ? '_blank' : undefined}
                  rel={p.link_url ? 'noopener noreferrer' : undefined}
                  className={styles.partnerLogoCard}
                >
                  {p.logo_path ? (
                    <img src={`${API_ORIGIN}${p.logo_path}`} alt={p.name} className={styles.partnerLogoImg} />
                  ) : (
                    <span>{p.name}</span>
                  )}
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* ---------- Our Product ---------- */}
      <section id="product" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Our product</div>
          <h2 className={styles.sectionTitle}>See what we're building.</h2>
          <p className={styles.sectionSub}>Product decks, whitepapers, and technical documentation — updated as we ship.</p>
        </Reveal>
        <div className={styles.docsGrid} style={{ ['--accent' as any]: '#1d60f6' }}>
          {productDocs.map((d) => {
            const isPdf = /\.pdf$/i.test(d.file_path);
            return (
              <Reveal key={d.id} className={styles.docCard}>
                <div className={styles.docIcon}>{isPdf ? <FiFileText /> : <FiImage />}</div>
                <div className={styles.docTitle}>{d.title}</div>
                <a className={styles.docLink} href={`${API_ORIGIN}${d.file_path}`} target="_blank" rel="noreferrer">
                  View document <FiArrowRight />
                </a>
              </Reveal>
            );
          })}
          {productDocs.length === 0 && <p className={styles.sectionSub}>Product documentation will be published here shortly.</p>}
        </div>
      </section>

      {/* ---------- Company Documents ---------- */}
      <section id="documents" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Legal &amp; Compliance</div>
          <h2 className={styles.sectionTitle}>Company documents.</h2>
          <p className={styles.sectionSub}>Incorporation and registration documents for Block Cost Technologies Private Limited.</p>
        </Reveal>
        <div className={styles.docsGrid} style={{ ['--accent' as any]: '#7c5cff' }}>
          {companyDocs.map((d) => {
            const isPdf = /\.pdf$/i.test(d.file_path);
            return (
              <Reveal key={d.id} className={styles.docCard}>
                <div className={styles.docIcon}>{isPdf ? <FiFileText /> : <FiImage />}</div>
                <div className={styles.docTitle}>{d.title}</div>
                <a
                  className={styles.docLink}
                  href={`${API_ORIGIN}${d.file_path}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View document <FiArrowRight />
                </a>
              </Reveal>
            );
          })}
          {companyDocs.length === 0 && <p className={styles.sectionSub}>Documents will be published here shortly.</p>}
        </div>
      </section>

      {/* ---------- Institutional Partner Program ---------- */}
      <section id="partners" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Institutional Partner Program</div>
          <h2 className={styles.sectionTitle}>
            {raiseTargetUsd ? `$${raiseTargetUsd >= 1000000 ? `${(raiseTargetUsd / 1000000).toLocaleString(undefined, { maximumFractionDigits: 1 })}M` : `${Math.round(raiseTargetUsd / 1000)}K`}` : '$4M'} institutional funds raising.
          </h2>
          <p className={styles.sectionSub}>Four partner tiers with equity share, priority terms, and a defined exit window.</p>
        </Reveal>
        <Reveal delay={80} className={styles.commissionBanner}>
          <h3>Institutional Partners Introducing Commission</h3>
          <p>Enjoying your institutional Partners Introducing Commission — flat 2.5% on every selected partner's positions.</p>
        </Reveal>
        <div className={styles.partnerGrid}>
          {partnerTiers.map((t) => (
            <Reveal
              key={t.key}
              className={`${styles.partnerCard} ${t.key === 'Executive' ? styles.partnerCardPopular : ''}`}
            >
              {t.key === 'Executive' && <div className={styles.partnerBadge}>Most popular</div>}
              <h3>{t.name}</h3>
              <div className={styles.partnerAmount}>${t.investCapital.toLocaleString()}</div>
              <div className={styles.partnerSeats}>{t.seatsAvailable} of {t.seats} seats available</div>
              <ul>
                <li><span>Program</span><b>{t.programYears} years</b></li>
                <li><span>Exit lock-in</span><b>{t.exitLockYears} years ({t.noticeDays}-day notice)</b></li>
                <li><span>Growth</span><b>{t.growth}</b></li>
                <li><span>Equity share</span><b>{t.equityTotalPct}%</b></li>
              </ul>
              <div className={styles.partnerEquityNote}>{t.equityNote}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- Meetings ---------- */}
      <section id="meetings" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Investor meetings</div>
          <h2 className={styles.sectionTitle}>Meet the team — Institution Investor Programme.</h2>
        </Reveal>
        <div className={styles.meetGrid}>
          <Reveal className={styles.meetCard}>
            <div className={styles.meetImg} style={{ backgroundImage: 'url(/images/meetings/physical-meet.jpeg)' }} />
            <div className={styles.meetBody}>
              <h4>Retail Investor Meetings</h4>
              <p>In-person sessions at our Dehradun office (or a city near you, on request) — walk through the deck, ask questions, and meet the founding team face to face.</p>
            </div>
          </Reveal>
          <Reveal delay={100} className={styles.meetCard}>
            <div className={styles.meetImg} style={{ backgroundImage: 'url(/images/meetings/virtual-meet.jpeg)' }} />
            <div className={styles.meetBody}>
              <h4>VC / venture meetings</h4>
              <p>Virtual calls for venture funds and institutional investors — deeper dive into structure, cap table, and diligence material, scheduled around your team's availability.</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={150}>
          <div className={styles.formPanel}>
            {meetingSubmitted ? (
              <div className={styles.formSuccess}>
                <FiCheckCircle size={22} />
                <div>
                  <b>Thanks — your meeting request has been recorded.</b>
                  <p>Our team will confirm the exact date, city/link and timing shortly.{' '}
                    <Link to="/register">Register your account</Link> with the same email to track its status.</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleMeetingSubmit}>
                <div className={styles.formTitle}>Request a meeting</div>
                <div className={styles.typeToggle}>
                  <button type="button" className={meetingType === 'physical' ? styles.typeActive : ''} onClick={() => setMeetingType('physical')}>Physical</button>
                  <button type="button" className={meetingType === 'vc' ? styles.typeActive : ''} onClick={() => setMeetingType('vc')}>VC / Venture</button>
                </div>
                <div className={styles.formGrid}>
                  <input name="fullName" placeholder="Full name" required />
                  <input name="email" type="email" placeholder="Email" required />
                  <input name="phone" placeholder="Phone (with country code)" required />
                  <input name="fundOrg" placeholder="Fund / organisation (if VC)" />
                  <input name="date" type="date" />
                  <input name="city" placeholder={meetingType === 'physical' ? 'City' : 'Time zone'} />
                  <select name="partnerTier" value={partnerTier} onChange={(e) => setPartnerTier(e.target.value)}>
                    <option value="">Interested Partner Tier (optional)</option>
                    {partnerTiers.map((t) => (
                      <option key={t.key} value={t.key}>{t.name} — ${(t.investCapital / 1000).toLocaleString()}K</option>
                    ))}
                  </select>
                </div>
                {partnerTier && (() => {
                  const t = partnerTiers.find((pt) => pt.key === partnerTier);
                  return t ? (
                    <div className={styles.seatHint}>
                      {t.seatsAvailable} of {t.seats} seats available for {t.name}
                    </div>
                  ) : null;
                })()}
                <textarea name="notes" placeholder="Anything specific you'd like to cover (optional)" rows={3} />
                {meetingError && <div className={styles.formError}>{meetingError}</div>}
                <button type="submit" className={styles.btnPrimary} disabled={meetingLoading}>
                  {meetingLoading ? 'Submitting…' : <>Request meeting <FiArrowRight /></>}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      {/* ---------- Our Pitch Deck ---------- */}
      <section id="pitch-deck" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Our pitch deck</div>
          <h2 className={styles.sectionTitle}>The full story, in one deck.</h2>
        </Reveal>
        <div className={styles.docsGrid} style={{ ['--accent' as any]: '#2bd4c8' }}>
          {pitchDeckDocs.map((d) => {
            const isPdf = /\.pdf$/i.test(d.file_path);
            return (
              <Reveal key={d.id} className={styles.docCard}>
                <div className={styles.docIcon}>{isPdf ? <FiFileText /> : <FiImage />}</div>
                <div className={styles.docTitle}>{d.title}</div>
                <a className={styles.docLink} href={`${API_ORIGIN}${d.file_path}`} target="_blank" rel="noreferrer">
                  View document <FiArrowRight />
                </a>
              </Reveal>
            );
          })}
          {pitchDeckDocs.length === 0 && <p className={styles.sectionSub}>Our pitch deck will be published here shortly.</p>}
        </div>
      </section>

      {/* ---------- Invest / Contact ---------- */}
      <section id="invest" className={styles.section}>
        <Reveal className={styles.sectionHead}>
          <div className={styles.eyebrow}>Register interest</div>
          <h2 className={styles.sectionTitle}>Ready to invest?</h2>
          <p className={styles.sectionSub}>Submit your interest below — our team will reach out to confirm allocation and KYC. Portal A starts from ₹10,000.</p>
        </Reveal>
        <Reveal className={styles.formPanel}>
          {investSubmitted ? (
            <div className={styles.formSuccess}>
              <FiCheckCircle size={22} />
              <div>
                <b>Thanks — your interest has been recorded.</b>
                <p>Our team will reach out on the phone/email provided.{' '}
                  <Link to="/register">Register your account</Link> with the same email to track its status.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleInvestSubmit}>
              <div className={styles.formTitle}>Investor interest form</div>
              <div className={styles.formGrid}>
                <input name="fullName" placeholder="Full name" required />
                <input name="email" type="email" placeholder="Email" required />
                <input name="phone" placeholder="Phone (with country code)" required />
                <input name="city" placeholder="City" />
                <Select
                  name="portal"
                  defaultValue="Portal A — 7% monthly"
                  onChange={(v) => setCalcPortal((['A', 'B', 'C'].includes(v.charAt(7)) ? v.charAt(7) : 'A') as 'A' | 'B' | 'C')}
                  options={[
                    { label: 'Portal A — 7% monthly', value: 'Portal A — 7% monthly' },
                    { label: 'Portal B — 5% monthly', value: 'Portal B — 5% monthly' },
                    { label: 'Portal C — 5% monthly profit', value: 'Portal C — 5% monthly profit' },
                  ]}
                />
                <Select
                  name="currency"
                  defaultValue="INR"
                  onChange={(v) => setCalcCurrency(v as 'INR' | 'USD')}
                  options={[
                    { label: 'INR (₹)', value: 'INR' },
                    { label: 'USD ($)', value: 'USD' },
                  ]}
                />
                <input
                  name="amount"
                  type="number"
                  placeholder="Investment amount"
                  required
                  value={calcAmount}
                  onChange={(e) => setCalcAmount(e.target.value)}
                />
                <input
                  name="referral"
                  placeholder="Referral code"
                  required
                  value={investReferral}
                  onChange={(e) => setInvestReferral(e.target.value.toUpperCase())}
                />
              </div>
              {defaultRefCode && (
                <div style={{ fontSize: 12, color: 'var(--muted, #9aa3b2)', marginTop: -8, marginBottom: 14 }}>
                  Don't have one?{' '}
                  <button
                    type="button"
                    onClick={() => setInvestReferral(defaultRefCode)}
                    style={{ color: '#d99a5f', fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    Use BlockCost's code ({defaultRefCode})
                  </button>
                </div>
              )}

              <div className={styles.calcBox}>
                {!calcAmount || !amountValid ? (
                  <div className={styles.calcWarning}>
                    {!calcAmount
                      ? 'Enter an amount within the allowed range to see estimated payouts.'
                      : `Amount is outside the allowed range for the selected currency (${fmt(range.min)} – ${fmt(range.max)}).`}
                  </div>
                ) : (
                  <div className={styles.calcGrid}>
                    <div><span>Monthly payout</span><b>{fmt(amountNum * portalCfg.monthly)} /mo</b></div>
                    <div><span>Total over {portalCfg.term} months (incl. capital)</span><b>{fmt(amountNum * (portalCfg.monthly * portalCfg.term + portalCfg.maturity))}</b></div>
                    <div><span>Instant BCT100 reward</span><b>{fmt(amountNum * portalCfg.token)}</b></div>
                  </div>
                )}
              </div>
              <textarea name="notes" placeholder="Anything you'd like our team to know (optional)" rows={3} />
              {investError && <div className={styles.formError}>{investError}</div>}
              <button type="submit" className={styles.btnPrimary} disabled={investLoading}>
                {investLoading ? 'Submitting…' : <>Submit interest <FiArrowRight /></>}
              </button>
            </form>
          )}
        </Reveal>
      </section>

      {/* ---------- Blog / Insights ---------- */}
      <section id="blog" className={`${styles.section} ${styles.blogSection}`}>
        <Reveal className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Insights. Trends. Ideas.<br />All About Web3.</h2>
        </Reveal>
        {!blogLoading && blogPosts.length === 0 && blogCategory === 'All Blogs' && !blogSearch ? (
          <div className={styles.comingSoon}>Coming Soon</div>
        ) : (
          <>
            <Reveal className={styles.blogSearchWrap}>
              <FiSearch className={styles.blogSearchIcon} />
              <input
                className={styles.blogSearchInput}
                placeholder="Search blogs, topics or keywords…"
                value={blogSearch}
                onChange={(e) => setBlogSearch(e.target.value)}
              />
            </Reveal>
            <Reveal className={styles.blogCategoryRow}>
              {['All Blogs', ...blogCategories].map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`${styles.blogCategoryPill} ${blogCategory === c ? styles.blogCategoryPillActive : ''}`}
                  onClick={() => setBlogCategory(c)}
                >
                  {c}
                </button>
              ))}
            </Reveal>
            {blogPosts.length === 0 ? (
              <div className={styles.comingSoon}>No posts found.</div>
            ) : (() => {
              const post = blogPosts[activeBlogIndex];
              if (!post) return null;
              return (
                <Reveal key={post.id} className={styles.blogCard}>
                  <Link to={`/blog/${post.id}`} className={styles.blogCardLinkWrap}>
                    {post.image_path && (
                      <div className={styles.blogCardImgWrap}>
                        <img src={`${API_ORIGIN}${post.image_path}`} alt={post.title} className={styles.blogCardImg} />
                        <span className={styles.blogCardTag}>{post.category}</span>
                      </div>
                    )}
                    <div className={styles.blogCardBody}>
                      <span className={styles.blogCardDate}>
                        {new Date(post.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </span>
                      <h3>{post.title}</h3>
                      {post.description && <p>{post.description}</p>}
                      <span className={styles.blogCardLink}>Read More <FiArrowRight /></span>
                    </div>
                  </Link>
                </Reveal>
              );
            })()}
            <Reveal className={styles.newsletterBar}>
              <div className={styles.newsletterLeft}>
                <div className={styles.newsletterIcon}><FiMail /></div>
                <div>
                  <b>Never Miss an Update</b>
                  <p>Subscribe to our newsletter and get the latest insights delivered straight to your inbox.</p>
                </div>
              </div>
              <form className={styles.newsletterForm} onSubmit={handleNewsletterSubmit}>
                <input
                  type="email"
                  required
                  placeholder="Enter your email address"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                />
                <button type="submit" disabled={newsletterSubmitting}>
                  {newsletterSubmitting ? 'Subscribing…' : <>Subscribe Now <FiArrowRight /></>}
                </button>
              </form>
            </Reveal>
          </>
        )}
      </section>

      {/* ---------- Our Story in Motion ---------- */}
      <section id="story" className={`${styles.section} ${styles.storySection}`}>
        <Reveal className={styles.sectionHead}>
          <h2 className={styles.sectionTitle}>Our Story in Motion</h2>
        </Reveal>
        {!storyLoading && storyVideos.length === 0 ? (
          <div className={styles.comingSoon}>Coming Soon</div>
        ) : (
          <Reveal className={styles.storyCarousel}>
            <div className={styles.storyMain}>
              <div className={styles.storyFrameRow}>
                <button
                  type="button"
                  className={styles.storyArrow}
                  disabled={storyVideos.length < 2}
                  onClick={() => setActiveVideoIndex((i) => (i - 1 + storyVideos.length) % storyVideos.length)}
                >
                  <FiChevronLeft />
                </button>
                <div key={storyVideos[activeVideoIndex]?.id} className={styles.storyVideoFrame}>
                  {storyVideos[activeVideoIndex]?.video_file_path ? (
                    <video
                      controls
                      poster={storyVideos[activeVideoIndex]?.thumbnail_path ? `${API_ORIGIN}${storyVideos[activeVideoIndex].thumbnail_path}` : undefined}
                      className={styles.storyVideoPlayer}
                    >
                      <source src={`${API_ORIGIN}${storyVideos[activeVideoIndex].video_file_path}`} />
                    </video>
                  ) : (
                    <>
                      {storyVideos[activeVideoIndex]?.thumbnail_path && (
                        <img src={`${API_ORIGIN}${storyVideos[activeVideoIndex].thumbnail_path}`} alt="" className={styles.storyThumb} />
                      )}
                      {storyVideos[activeVideoIndex]?.video_url ? (
                        <a href={storyVideos[activeVideoIndex].video_url} target="_blank" rel="noopener noreferrer" className={styles.storyPlayBtn}>
                          <FiPlay />
                        </a>
                      ) : (
                        <div className={styles.storyPlayBtn}><FiPlay /></div>
                      )}
                    </>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.storyArrow}
                  disabled={storyVideos.length < 2}
                  onClick={() => setActiveVideoIndex((i) => (i + 1) % storyVideos.length)}
                >
                  <FiChevronRight />
                </button>
              </div>
              <h3 className={styles.storyTitle}>{storyVideos[activeVideoIndex]?.title}</h3>
              {storyVideos[activeVideoIndex]?.disclaimer && (
                <p className={styles.storyDisclaimer}>{storyVideos[activeVideoIndex].disclaimer}</p>
              )}
            </div>
          </Reveal>
        )}
      </section>

      <footer id="contact" className={styles.footer}>
        <div className={styles.footerTop}>
          <div className={styles.footerBrand}>
            <div className={styles.brand}>
              <img src="/images/logo.png" alt="BCT" className={styles.brandLogo} />
              Block Cost Technologies
            </div>
            <p className={styles.footerTagline}>Blockchain, AI-agent, fintech and Web3 infrastructure — built and operated in-house.</p>
            <div className={styles.footerSocials}>
              <a href="https://twitter.com" target="_blank" rel="noreferrer" aria-label="Twitter / X"><FiTwitter /></a>
              <a href="https://linkedin.com" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FiLinkedin /></a>
              <a href="https://t.me" target="_blank" rel="noreferrer" aria-label="Telegram"><FiSend /></a>
            </div>
          </div>
          <div className={styles.footerCol}>
            <h5>Company</h5>
            <a href="#opportunity">The opportunity</a>
            <a href="#portals">Investment portals</a>
            <a href="#services">Services</a>
            <a href="#structure">Corporate structure</a>
          </div>
          <div className={styles.footerCol}>
            <h5>Resources</h5>
            <a href="#product">Our product</a>
            <a href="#documents">Company documents</a>
            <a href="#pitch-deck">Pitch deck</a>
            <a href="#partners">Partner program</a>
          </div>
          <div className={styles.footerCol}>
            <h5>Contact</h5>
            <span><FiMapPin /> Dehradun, India</span>
            <a href="mailto:support@blockcostlabs.com"><FiMail /> support@blockcostlabs.com</a>
            <a href="#meetings">Request a meeting</a>
          </div>
        </div>
        <div className={styles.footerBottom}>
          © {new Date().getFullYear()} Block Cost Technologies. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Landing;